import { useMemo } from 'react'

import { dequal } from 'dequal'
import {
  IconFileBend,
  Inspector,
  GreyGrey70
} from '@sequencehq/core-components'
import { atom, useAtom, useAtomValue } from 'jotai'

import {
  IntegrationServices,
  ServerAsset,
  useQuery
} from '@sequencehq/api/utils'
import {
  dashboardv99990101Client,
  Dashboardv99990101CommonModels
} from '@sequencehq/api/dashboard/v99990101'

import {
  LinkNetsuiteCustomerWidget,
  LinkNetsuiteInvoiceWidget,
  LinkQuickbooksCustomerWidget,
  LinkQuickbooksInvoiceWidget,
  LinkRilletCustomerWidget,
  LinkRilletInvoiceWidget,
  LinkSalesforceAccountWidget,
  LinkSalesforceInvoiceWidget,
  LinkXeroCustomerWidget,
  LinkXeroInvoiceWidget,
  NetsuiteInspectorItem,
  QuickBooksInspectorItem,
  RilletInspectorItem,
  SalesforceInspectorItem,
  XeroInspectorItem
} from 'Integrations/index'
import { showAttachmentInInspectorAtom } from 'Assets/Attachments'
import { InvoiceAttachments } from 'modules/Invoices'
import {
  clientInvoiceAtom,
  isSavePendingAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { InvoiceStripeInspectorItem } from './InvoiceStripeInspectorItem'
import { InvoiceGoCardlessInspectorItem } from './InvoiceGoCardlessInspectorItem'
import { usePermissions } from 'lib/rbac'
import { ActivityLogsInspectorItem } from 'ActivityLogs/index'
import { WorkflowsInspectorItem } from 'Automations/index'

// Allows external components to trigger the inspector to open a specific item
export const triggeredInspectorItemIdAtom = atom<string | null>(null)

const serviceHasLinkedCustomer =
  (service: IntegrationServices) =>
  (
    customerLinks: Dashboardv99990101CommonModels.Customers.Customer['integrationIds']
  ) => {
    return Boolean(customerLinks.find(cl => cl.service === service)?.id)
  }

const InvoiceAttachmentsInspectorItem = () => {
  const [clientInvoice, partialUpdateInvoice] = useAtom(clientInvoiceAtom)
  const attachmentIdToView = useAtomValue(showAttachmentInInspectorAtom)
  const isSavePending = useAtomValue(isSavePendingAtom)
  const { can } = usePermissions()

  const attachments = clientInvoice?.attachmentAssets || []

  const attachmentToView = useMemo(() => {
    if (!attachmentIdToView) {
      return undefined
    }

    return attachments.find(attachment => attachment.id === attachmentIdToView)
  }, [attachmentIdToView, attachments])

  const handleAttachmentChange = (assets: ServerAsset[]) => {
    if (
      !dequal(
        assets.map(({ id }) => id),
        attachments.map(({ id }) => id)
      )
    ) {
      partialUpdateInvoice({ attachmentAssets: assets })
    }
  }

  return (
    <InvoiceAttachments
      attachmentToPreview={attachmentToView ?? undefined}
      initialAttachments={attachments}
      onChange={handleAttachmentChange}
      disableModifyAttachments={
        !clientInvoice || isSavePending
          ? { reason: '' }
          : !can('org.invoice:update')
            ? {
                reason:
                  'You need invoice edit permissions to modify attachments'
              }
            : clientInvoice.status !== 'DRAFT'
              ? { reason: 'Attachments can only be modified on draft invoices' }
              : undefined
      }
    />
  )
}

export const InvoiceInspector = () => {
  const clientInvoice = useAtomValue(clientInvoiceAtom)
  const attachmentToView = useAtomValue(showAttachmentInInspectorAtom)

  const customerIntegrationsQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: clientInvoice?.customerId ?? '' },
    {
      enabled: !!clientInvoice?.customerId,
      select: customer => {
        if (!customer) return null
        return customer.integrationIds
      }
    }
  )

  const hasLinkedCustomer = customerIntegrationsQuery.data
    ? {
        xero: serviceHasLinkedCustomer('Xero')(customerIntegrationsQuery.data),
        quickbooks: serviceHasLinkedCustomer('QuickBooks_Online')(
          customerIntegrationsQuery.data
        ),
        rillet: serviceHasLinkedCustomer('Rillet')(
          customerIntegrationsQuery.data
        ),
        salesforce: serviceHasLinkedCustomer('Salesforce')(
          customerIntegrationsQuery.data
        ),
        netsuite: serviceHasLinkedCustomer('NetSuite')(
          customerIntegrationsQuery.data
        )
      }
    : {
        xero: false,
        quickbooks: false,
        rillet: false,
        salesforce: false,
        netsuite: false
      }

  const ATTACHMENTS_ITEM_ID = 'attachments.inspector.item'

  const triggeredInspectorItemId = useAtomValue(triggeredInspectorItemIdAtom)

  return (
    <Inspector
      withFloatingDrawer
      showItemId={
        triggeredInspectorItemId ??
        (attachmentToView ? ATTACHMENTS_ITEM_ID : undefined)
      }
      expanded={triggeredInspectorItemId || attachmentToView ? true : undefined}
    >
      <Inspector.ItemGroup>
        <Inspector.IconItem
          id={ATTACHMENTS_ITEM_ID}
          data-testid="attachments.inspector.item"
          tooltip="Attachments"
          content={<InvoiceAttachmentsInspectorItem />}
          icon={<IconFileBend color={GreyGrey70} />}
        />
        {clientInvoice && (
          <>
            <ActivityLogsInspectorItem
              enabled
              entityId={clientInvoice.id}
              entityType="INVOICE"
            />
            {clientInvoice.status !== 'DRAFT' && (
              <WorkflowsInspectorItem
                entityId={clientInvoice.id}
                entityType="INVOICE"
              />
            )}
            <XeroInspectorItem>
              <LinkXeroCustomerWidget customerId={clientInvoice.customerId} />
              {hasLinkedCustomer.xero && (
                <LinkXeroInvoiceWidget invoiceId={clientInvoice.id} />
              )}
            </XeroInspectorItem>
            <QuickBooksInspectorItem>
              <LinkQuickbooksCustomerWidget
                customerId={clientInvoice.customerId}
              />
              {hasLinkedCustomer.quickbooks && (
                <LinkQuickbooksInvoiceWidget invoiceId={clientInvoice.id} />
              )}
            </QuickBooksInspectorItem>
            <NetsuiteInspectorItem>
              <LinkNetsuiteCustomerWidget
                customerId={clientInvoice.customerId}
              />
              {hasLinkedCustomer.netsuite && (
                <LinkNetsuiteInvoiceWidget invoiceId={clientInvoice.id} />
              )}
            </NetsuiteInspectorItem>
            <RilletInspectorItem>
              <LinkRilletCustomerWidget customerId={clientInvoice.customerId} />
              {hasLinkedCustomer.rillet && (
                <LinkRilletInvoiceWidget invoiceId={clientInvoice.id} />
              )}
            </RilletInspectorItem>
            <SalesforceInspectorItem>
              <LinkSalesforceAccountWidget
                customerId={clientInvoice.customerId}
              />
              {hasLinkedCustomer.salesforce && (
                <LinkSalesforceInvoiceWidget invoiceId={clientInvoice.id} />
              )}
            </SalesforceInspectorItem>
            <InvoiceStripeInspectorItem />
            <InvoiceGoCardlessInspectorItem />
          </>
        )}
      </Inspector.ItemGroup>
    </Inspector>
  )
}
